<script setup lang="ts">
import { ref, reactive, onMounted, onUnmounted, computed } from "vue";
import reviewRepository, { ICreateReview } from "@/repositories/review.repository";
import sportaanbiederRepository from "@/repositories/sportaanbieder.repository";
import { useProfileStore } from "@/stores/profile.store";
import { usePlatformStore } from "@/stores/platform.store";
import Modal from "./Modal.vue";
import { useDebounceFn } from "@vueuse/core";
import { useUrlSearchParams } from "@/helpers/url.helper";
import { email, helpers, maxLength, minLength, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { useValidationHelpers } from "@/helpers/validation.helper";


const profileStore = useProfileStore();
const platformStore = usePlatformStore();

const params = useUrlSearchParams()

const props = defineProps<{
    show: boolean;
    sportaanbiederId?: string;
    sportactiviteitId?: number;
}
>();

const emits = defineEmits<{
    (e: "close"): void;
}>();

/* ---------------- state ---------------- */
const loading = ref(false);
const success = ref(false);
const error = ref(false);
const alReviewAchtergelaten = ref(false);

const sportaanbiederSelected = ref(!!props.sportaanbiederId);
const sportaanbieders = ref<any[]>([]);
const loadingSportaanbieders = ref(false);
const hasSearched = ref(false);

const reviewVragenList = ref<any[]>([]);

const searchTerm = ref("");


// reactive review met type
const review = reactive<ICreateReview>({
    Approved: true,
    Recommend: null,
    Title: "",
    Description: "",
    Alias: params.getParam("naam") ?? "",
    Email: params.getParam("emailadres") ?? "",
    EntityType: "Sportaanbieder",
    EntityId: props.sportaanbiederId,
    SportactiviteitId: props.sportactiviteitId,
    Antwoorden: [],
    Source: window.location.host,
    Platform: platformStore.platformId,
});


/* ---------------- constants ---------------- */
const values = [
    { Waarde: 1, Beschrijving: "Slecht" },
    { Waarde: 2, Beschrijving: "Matig" },
    { Waarde: 3, Beschrijving: "Gemiddeld" },
    { Waarde: 4, Beschrijving: "Goed" },
    { Waarde: 5, Beschrijving: "Uitstekend" },
];

/* ---------------- init ---------------- */
onMounted(async () => {
    const vragen = await reviewRepository.getReviewVragen();

    vragen.forEach(vraag => {
        vraag.Antwoord = null
    });

    reviewVragenList.value = vragen;

    if (profileStore.isLoggedIn) {
        profileStore.ensureProfile();
        review.Alias ||= profileStore.profile?.Voornaam ?? "";
        review.Email ||= profileStore.profile?.Emailadres ?? "";
    }
});


/* ---------------- validations ---------------- */
const rules = computed(() => ({
    review: {
        Alias: { required },
        Email: { required, email },
        Title: { required, minLength: minLength(3), maxLength: maxLength(150) },
        Description: { maxLength: maxLength(1000) },
        Recommend: { required },
    },

    reviewVragenList: {
        $each: helpers.forEach({
            Antwoord: { required: helpers.withMessage("Dit veld is verplicht", required) }
        })
    }
}));

const v$ = useVuelidate(rules, {
    review,
    reviewVragenList
});

const { getFieldErrors } = useValidationHelpers(v$);

/* ---------------- actions ---------------- */
const selectSportaanbieder = (item: any) => {
    review.EntityId = item.Id;
    sportaanbiederSelected.value = true;
};

const searchSportaanbieder = async (typed: string) => {
    if (!typed || typed.length < 3) {
        sportaanbieders.value = [];
        return;
    }

    loadingSportaanbieders.value = true;
    sportaanbieders.value = await sportaanbiederRepository.searchSportaanbiederByNaam(typed, platformStore.platformId);
    loadingSportaanbieders.value = false;
    hasSearched.value = true;
};

const debouncedSearch = useDebounceFn(() => {
    searchSportaanbieder(searchTerm.value);
}, 500);

const submit = async () => {
    v$.value.$touch();
    const valid = await v$.value.$validate();
    if (!valid) return;

    if (loading.value) return;
    loading.value = true;

    try {
        const exists = await reviewRepository.getAlReviewAchtergelaten(
            review.EntityId!,
            review.EntityType,
            review.Email
        );

        if (exists) {
            alReviewAchtergelaten.value = true;
            return;
        }

        review.Antwoorden = reviewVragenList.value.map((item) => ({
            ReviewVraag: item.Id,
            Antwoord: item.Antwoord,
        }));

        const result = await reviewRepository.createReview(review);

        if (result?.Id) {
            success.value = true;
        } else {
            error.value = true;
        }
    } catch (err) {
        console.error("Fout bij versturen review:", err);
        error.value = true;
    } finally {
        loading.value = false;
    }
};


const resetReviewFields = () => {
    review.Antwoorden = [];
    review.Title = "";
    review.Description = "";
    review.Recommend = null;
    review.Alias = params.getParam("naam") ?? "";
    review.Email = params.getParam("emailadres") ?? "";

    // reset alle antwoorden in reviewVragenList
    reviewVragenList.value.forEach((vraag) => {
        vraag.Antwoord = null;
    });

    v$.value.$reset();
};

const cancel = () => {
    resetReviewFields();
    emits("close");
};

onUnmounted(() => {
    resetReviewFields();
});
</script>

<template>
    <Modal :show="props.show" @close="cancel">
        <div class="sport-in-modal club-review-modal">
            <div class="sport-in-modal-header">
                <h2 class="sport-in-modal-heading h4">Laat een review achter</h2>
                <button class="sport-in-close-modal-btn" @click="cancel" aria-label="Sluit modal">
                    <span class="icon-close"></span>
                </button>
            </div>

            <div class="sport-in-modal-body">
                <div v-if="!sportaanbiederSelected && !success">
                    <p>Over welke club wil jij een review achterlaten?</p>
                    <p>Zoek hieronder een club.</p>

                    <div class="form-group has-feedback" :class="{
                        'has-success': searchTerm.length >= 3 && sportaanbieders.length === 0,
                        'has-error': searchTerm.length >= 3 && sportaanbieders.length === 0 && !loadingSportaanbieders
                    }">
                        <input type="text" v-model="searchTerm" @input="debouncedSearch" placeholder="Club"
                            class="form-control" />
                        <span v-if="loadingSportaanbieders" class="glyphicon glyphicon-refresh spinning"></span>
                    </div>

                    <ul class="fancy-list-group" v-if="sportaanbieders.length">
                        <li v-for="sp in sportaanbieders" :key="sp.Id"
                            class="fancy-list-group-item fancy-list-group-item-hover">
                            <button @click="selectSportaanbieder(sp)" class="d-flex align-items-center">
                                <img class="img-fluid" :src="sp.LogoUrl + '?height=100&mode=crop'" alt="" />
                                <div class="ms-2">
                                    <p class="fancy-subtitle font-weight-bold">{{ sp.Naam }}</p>
                                    <span class="link-with-icon">Selecteer deze club <span
                                            class="icon icon-arrow--right"></span></span>
                                </div>
                            </button>
                        </li>
                    </ul>

                    <p v-if="hasSearched && !sportaanbieders.length">
                        Er zijn geen deelnemende clubs gevonden die voldoen aan jouw zoekopdracht.
                    </p>
                </div>

                <div v-else-if="sportaanbiederSelected && !success">
                    <ul class="list-inline">
                        <li v-for="(vraag, index) in reviewVragenList" :key="vraag.Id"
                            class="form-group ic-review-list">
                            <h3 class="ic-review-title">{{ vraag.Titel }}</h3>
                            <p class="control-label ic-review-desc">{{ vraag.Vraag }}</p>
                            <div class="ic-review-label-wrapper">
                                <div class="ic-review-label-button" v-for="value in values" :key="value.Waarde">
                                    <input type="radio" :id="'reviewVraag' + vraag.Id + '_Antwoord' + value.Waarde"
                                        :name="vraag.Id" v-model="vraag.Antwoord" :value="value.Waarde" />
                                    <label :for="'reviewVraag' + vraag.Id + '_Antwoord' + value.Waarde"
                                        class="btn btn-white btn-alt">
                                        {{ value.Beschrijving }}
                                    </label>
                                </div>
                                <template v-if="v$.$dirty">
                                    <div v-for="err in v$.reviewVragenList.$each.$response.$errors[index]?.Antwoord"
                                        :key="err" class="ic-error" role="alert">
                                        <small class="text-danger">{{ err.$message }}</small>
                                    </div>
                                </template>
                            </div>
                        </li>
                    </ul>

                    <ul class="list-inline">
                        <li class="form-group ic-review-list">
                            <h3 class="ic-review-title">Zou jij deze club aan anderen aanbevelen?</h3>
                            <div class="ic-review-label-wrapper">
                                <div class="ic-review-label-button">
                                    <input type="radio" v-model="review.Recommend" :value="true" id="recommendJa" />
                                    <label for="recommendJa" class="btn ic-review-true">Ja <span
                                            class="icon icon-thumbs-up"></span></label>
                                </div>
                                <div class="ic-review-label-button">
                                    <input type="radio" v-model="review.Recommend" :value="false" id="recommendNee" />
                                    <label for="recommendNee" class="btn ic-review-false">Nee <span
                                            class="icon icon-thumbs-down"></span></label>
                                </div>
                                <div v-for="err in getFieldErrors('review.Recommend')" class="ic-error" role="alert">
                                    <small class="text-danger">{{ err }}</small>
                                </div>
                            </div>
                        </li>
                    </ul>

                    <div class="ic-review-list">
                        <h3 class="ic-review-title">Extra informatie</h3>
                    </div>
                    <div class="row">
                        <div class="col-md-12">
                            <div class="form-group">
                                <label class="control-label" for="alias">Getoonde naam *</label>

                                <div class="help-tooltip euro-tooltip pull-right" tooltip-placement="top-right"
                                    v-tooltip="'Deze naam wordt zichtbaar bij je review voor andere bezoekers. Gebruik bijvoorbeeld je voornaam of een bijnaam. Je e-mailadres wordt niet openbaar getoond.'">
                                    Meer informatie <span class="icon-help" aria-hidden="true"></span>
                                </div>

                                <input id="alias" type="text" class="form-control" v-model="review.Alias" />
                                <div v-for="err in getFieldErrors('review.Alias')" class="ic-error" role="alert">
                                    <small class="text-danger">{{ err }}</small>
                                </div>

                            </div>
                        </div>
                        <div class="col-md-12">
                            <div class="form-group">
                                <label class="control-label" for="email">E-mailadres *</label>

                                <div class="help-tooltip euro-tooltip pull-right" tooltip-placement="top-right"
                                    v-tooltip="'We gebruiken je e-mailadres alleen om je te informeren dat je review is ontvangen en wanneer deze wordt gepubliceerd. Je e-mailadres wordt nooit openbaar getoond.'">
                                    Meer informatie <span class="icon-help" aria-hidden="true"></span>
                                </div>

                                <input id="email" type="email" class="form-control" v-model="review.Email" />
                                <div v-for="err in getFieldErrors('review.Email')" class="ic-error" role="alert">
                                    <small class="text-danger">{{ err }}</small>
                                </div>

                            </div>
                        </div>

                        <div class="col-md-12">
                            <div class="form-group">

                                <label class="control-label" for="titel">Titel van je review *</label>

                                <div class="help-tooltip euro-tooltip pull-right" tooltip-placement="top-right"
                                    v-tooltip="'Geef je review een korte, duidelijke titel die samenvat wat jouw ervaring is, bijvoorbeeld over de sfeer, trainers of faciliteiten.'"">
                                    Meer informatie <span class=" icon-help" aria-hidden="true"></span>
                                </div>

                                <input id="titel" type="text" class="form-control" v-model="review.Title" />
                                <div v-for="err in getFieldErrors('review.Title')" class="ic-error" role="alert">
                                    <small class="text-danger">{{ err }}</small>
                                </div>

                            </div>
                        </div>

                        <div class="col-md-12">
                            <div class="form-group">
                                <label class="control-label" for="beschrijving">Toelichting</label>

                                <div class="help-tooltip euro-tooltip pull-right" tooltip-placement="top-right"
                                    v-tooltip="'Beschrijf hier je ervaring uitgebreider. Vertel wat goed ging en wat eventueel beter kan. Houd je tekst concreet, respectvol en relevant voor andere bezoekers.'">
                                    Meer informatie <span class="icon-help" aria-hidden="true"></span>
                                </div>

                                <textarea id="beschrijving" class="form-control"
                                    v-model="review.Description"></textarea>
                                <div v-for="err in getFieldErrors('review.Description')" class="ic-error" role="alert">
                                    <small class="text-danger">{{ err }}</small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <section v-if="alReviewAchtergelaten" class="bg-danger text-center p-2 my-2">
                    Je hebt al een review achtergelaten voor deze club.
                </section>

                <section v-if="error" class="bg-danger text-center p-2 my-2">
                    Er is iets mis gegaan bij het plaatsen van jouw review.
                </section>

                <section v-if="success" class="bg-success text-center p-2 my-2">
                    Je review is geplaatst!
                </section>
            </div>

            <div class="sport-in-modal-footer">
                <button class="btn btn-sm btn-sport-in-light" @click="cancel">
                    {{ success ? "Sluiten" : "Annuleren" }}
                </button>
                <button class="btn btn-sm btn-primary" @click="submit" :disabled="loading || alReviewAchtergelaten"
                    v-if="!success">
                    <span v-if="loading" class="glyphicon glyphicon-refresh spinning"></span>
                    Verstuur
                </button>
            </div>
        </div>
    </Modal>
</template>